import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useFetchData } from 'services/api/useFetchData';
import { Division } from '../environments/EnvironmentTypes';
import DropdownButton from 'components/buttons/DropdownButton';

// Value used to request all divisions combined
export const ALL_DIVISIONS_VALUE = 'all';

interface DivisionSelectorProps {
    selectedDivision: string;
    onChange: (division: string) => void;
}

const DivisionSelector: React.FC<DivisionSelectorProps> = ({ selectedDivision, onChange }) => {
    const { t } = useTranslation();
    const { userDivision, activeFeatures } = useAuthContext();

    // Only fetch the division list when the divisions feature is active and the user isn't locked to one
    const hasDivisionsFeature = activeFeatures.includes('divisions');
    const shouldFetchDivisions = hasDivisionsFeature && !userDivision;
    const { response } = useFetchData({
        apiUrl: 'get_division_list',
        params: useMemo(() => ({ is_active: true }), []),
        useGuard: true,
        guardValue: shouldFetchDivisions ? true : null,
    });

    const divisions: Division[] = response?.results ?? [];

    // Hide the selector for users locked to a single division, without the feature, or with 1 or less divisions
    if (userDivision || !hasDivisionsFeature || divisions.length <= 1) {
        return null;
    }

    // Find the label of the currently selected division, or the "all divisions" label
    const activeDivision = divisions.find(division => division.id.toString() === selectedDivision);
    const activeLabel = activeDivision ? activeDivision.name : t('dashboard.general.all_divisions_label');

    const items = [
        {
            label: t('dashboard.general.all_divisions_label'),
            onClick: () => onChange(ALL_DIVISIONS_VALUE),
        },
        ...divisions.map(division => ({
            label: division.name,
            onClick: () => onChange(division.id.toString()),
        })),
    ];

    return (
        <DropdownButton
            items={items}
            label={activeLabel}
            activeItem={activeLabel}
            size='small'
        />
    );
};

export default DivisionSelector;
